import React, { useState, useEffect, useMemo } from "react";
import { accountAPI, api } from "services";
import { ProfilePhoto, MyPhoneInput } from "components";
import Creatable from "react-select/creatable";
import { creatableStyle } from "assets/themes";
import { toast } from "sonner";
import useUniversalStore from "store/universalStore";
import useOffcanvasStore from "store/offcanvasStore";
import { useForm, Controller, useWatch } from "react-hook-form";
import { defaultValues, validateAccountName } from "./config";
import { getAllTagsFromAccounts, validatePhone } from "utils";

const nameRules = {
  required: "Name is required",
  maxLength: { value: 50, message: "Name cannot exceed 50 characters" },
  validate: validateAccountName
};

const phoneRules = {
  validate: (value) => {
    if (!value || value === "+91") return true; // to make phone number optional
    return validatePhone(value);
  }
};

const tagRules = {
  maxLength: { value: 20, message: "Tag cannot exceed 20 characters" }
};

const NewAccount = () => {
  const { write, accounts } = useUniversalStore((state) => state);
  const { closeOffcanvas } = useOffcanvasStore();
  const { control, handleSubmit, setValue, register, formState: { errors, isSubmitting, isValid } } = useForm({
    defaultValues,
    mode: "onBlur"
  });

  const photo = useWatch({ control, name: "photo" });
  const phone = useWatch({ control, name: "phone" });

  const tags = getAllTagsFromAccounts(accounts);

  const mappedTags = tags.map((tag) => ({ value: tag, label: tag?.toUpperCase() }));

  const [contacts, setContacts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const fetchContacts = async () => {
      setIsFetching(true);
      try {
        const res = await api.getContacts();
        if (res?.[0]) {
          const phonebook = res[0].get("phonebook") || [];
          setContacts(phonebook);
        }
      } catch (error) {
        console.error("Failed to fetch contacts", error);
      } finally {
        setIsFetching(false);
      }
    };
    fetchContacts();
  }, []);

  const contactOptions = useMemo(() => {
    return contacts
      .filter((c) => c?.name)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({
        value: c?.number?.replace(/\s+/g, ""),
        label: c?.name
      }));
  }, [contacts]);

  const handleContactSelect = (selectedOption) => {
    if (!selectedOption) {
      setValue("name", "", { shouldValidate: true });
      setValue("phone", "");
      return;
    }
    setValue("name", selectedOption.label, { shouldValidate: true });
    if (!selectedOption.__isNew__) {
      setValue("phone", selectedOption.value || "");
    }
  };

  const onSubmit = async (data) => {
    try {
      const result = await accountAPI.createAccount(
        data.name,
        data.phone,
        data.address,
        data.details,
        data.tag?.value ?? null,
        data.photo.url
      );

      write({
        accounts: await accountAPI.getAccounts(),
        accountId: result.id
      });

      toast.success("Account created successfully");
      closeOffcanvas();
    } catch (error) {
      toast.error("Failed to create account");
    }
  };

  const inputClass = (name) => `form-control ${errors[name] ? "is-invalid" : ""}`;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mt-2" align="center">
        <ProfilePhoto
          key="add-profile-photo"
          url={photo?.url}
          loading={photo?.loading || isSubmitting}
          updateField={(field, data) => setValue(field, data)}
          disabled={isSubmitting}
        />
      </div>

      <div className="mb-2 mb-md-3">
        <Controller
          name="name"
          control={control}
          rules={nameRules}
          render={({ field: { value } }) => (
            <Creatable
              options={contactOptions}
              value={value ? { label: value, value: phone } : null}
              classNamePrefix="rs"
              isLoading={isFetching}
              isClearable
              placeholder="Account Name"
              onChange={handleContactSelect}
              classNames={creatableStyle}
              className={errors.name ? "is-invalid" : value ? "is-valid" : ""}
              isDisabled={isSubmitting}
            />
          )}
        />
        {errors.name && <div className="invalid-feedback d-block">{errors.name.message}</div>}
      </div>

      <div className="mb-2 mb-md-3">
        <div className="phone-input-wrapper">
          <Controller
            name="phone"
            control={control}
            rules={phoneRules}
            render={({ field }) => (
              <MyPhoneInput
                field={field}
                error={errors.phone}
                isSubmitting={isSubmitting}
              />
            )}
          />
        </div>
        {errors.phone && <div className="invalid-feedback d-block">{errors.phone.message}</div>}
      </div>

      <div className="mb-2 mb-md-3">
        <Controller
          name="tag"
          control={control}
          rules={tagRules}
          render={({ field: { value, onChange } }) => (
            <Creatable
              isClearable
              options={mappedTags}
              value={value}
              onChange={onChange}
              placeholder="Select or create a Tag"
              classNamePrefix="rs"
              classNames={creatableStyle}
              className={errors.tag ? "is-invalid" : value ? "is-valid" : ""}
              isDisabled={isSubmitting}
            />
          )}
        />
        {errors.tag && <div className="invalid-feedback d-block">{errors.tag.message}</div>}
      </div>

      <div className="mb-2 mb-md-3">
        <div className="form-control border-0 p-0">
          <input
            {...register("address")}
            type="text"
            placeholder="Address"
            className={inputClass("address")}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className="mb-5 pb-5">
        <div className="form-control border-0 p-0">
          <textarea
            {...register("details")}
            placeholder="Other Details"
            className={inputClass("details")}
            disabled={isSubmitting}
            rows={2}
          />
        </div>
      </div>

      <div className="px-3 position-absolute bottom-0 start-0 end-0 mb-3">
        <button
          type="submit"
          className="btn btn-app w-100 py-2 py-md-3 fs-5 fw-bold text-uppercase"
          disabled={isSubmitting || photo?.loading || !isValid}
        >
          {photo?.loading ? (
            <span className="spinner-border spinner-border-sm me-2" />
          ) : isSubmitting ? "Adding..." : "Add"}
        </button>
      </div>
    </form>
  );
};

export default NewAccount;
